// src/pages/PasswordsPage.js

import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, addDoc, onSnapshot, doc, deleteDoc, updateDoc, getDocs, where } from 'firebase/firestore';
import { db } from '../App';
import { appId } from '../config/firebaseConfig';
import { PASSWORD_CATEGORIES, PASSWORD_CATEGORY_KEYS } from '../config/passwordConfig';
import useConfirmationModal from '../components/common/useConfirmationModal';
import TOTPDisplay from '../components/common/TOTPDisplay';
import PasswordGeneratorInput from '../components/common/PasswordGeneratorInput';
import QrCodeInput from '../components/common/QrCodeInput';
import PasswordInput from '../components/common/PasswordInput';
import PasswordDisplay from '../components/common/PasswordDisplay';
import LinkifyText from '../components/common/LinkifyText';

// --- PÁGINA DE SENHAS ---
export default function PasswordsPage({ userId, permissionLevel }) {
    const [passwords, setPasswords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [categorySelectionMode, setCategorySelectionMode] = useState(false);
    const [formMode, setFormMode] = useState(null);
    const [currentItem, setCurrentItem] = useState(null);
    const [viewItem, setViewItem] = useState(null);
    const [categorySearchText, setCategorySearchText] = useState({});
    
    const { ConfirmationModal, showConfirmation } = useConfirmationModal();

    const passwordsCollectionPath = useMemo(() => `/artifacts/${appId}/users/${userId}/passwords`, [userId]);

    useEffect(() => {
        const qPasswords = query(collection(db, passwordsCollectionPath));
        const unsubscribePasswords = onSnapshot(qPasswords, (snapshot) => {
            setPasswords(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        }, (error) => {
            console.error("Erro ao carregar senhas (Listener):", error);
            setLoading(false);
        });

        return () => unsubscribePasswords();
    }, [passwordsCollectionPath]);

    const handleOpenForm = (category, mode = 'add', item = null) => {
        if (permissionLevel !== 'edit') return;
        setCategorySelectionMode(false);
        setFormMode({ mode, category });
        setCurrentItem(item);
        setViewItem(null);
    };

    const handleCloseForm = () => {
        setFormMode(null);
        setCurrentItem(null);
    };

    const handleDeletePassword = (passwordId, serviceName) => {
        if (permissionLevel !== 'edit') return;
        showConfirmation({
            title: "Excluir Senha?",
            message: `Tem a certeza que deseja apagar a senha para "${serviceName}"?`,
            onConfirm: async () => {
                await deleteDoc(doc(db, passwordsCollectionPath, passwordId));
                setViewItem(null);
            }
        });
    };
    
    const getPasswordDisplayName = (pw) => {
        if (!pw || !pw.data) return 'Entrada Inválida';
        const categoryConfig = PASSWORD_CATEGORIES[pw.category];
        if (!categoryConfig) return 'Categoria Inválida';
        const primaryField = categoryConfig.fields[0].name;
        return pw.data[primaryField] || 'Nome Indefinido';
    };
    
    const getPasswordDisplaySubtext = (pw) => {
        if (!pw || !pw.data) return '';
        const categoryConfig = PASSWORD_CATEGORIES[pw.category];
        if (categoryConfig.fields.length < 2) return '';
        const secondaryField = categoryConfig.fields[1].name;
        return pw.data[secondaryField] || '';
    };

    const groupedPasswords = useMemo(() => {
        const grouped = {};
        PASSWORD_CATEGORY_KEYS.forEach(key => {
            const list = passwords.filter(p => p.category === key).sort((a,b) => getPasswordDisplayName(a).localeCompare(getPasswordDisplayName(b)));
            if (list.length > 0) {
                grouped[key] = {
                    ...PASSWORD_CATEGORIES[key],
                    passwords: list
                };
            }
        });
        return grouped;
    }, [passwords]);


    return (
        <div className="min-h-screen pt-8 md:pt-12 px-4 sm:px-6 md:px-8 pb-20">
            <ConfirmationModal />
            
            {categorySelectionMode && permissionLevel === 'edit' && (
                 <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-md flex justify-center items-center z-50 p-4 animate-in fade-in duration-300">
                    <div className="bg-slate-900 border border-slate-700 rounded-3xl p-8 shadow-2xl w-full max-w-3xl animate-in zoom-in-95 duration-300">
                        <h3 className="text-2xl font-black text-cyan-300 mb-8 text-center uppercase tracking-widest">Selecione uma Categoria</h3>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                            {PASSWORD_CATEGORY_KEYS.map(key => (
                                <button 
                                    key={key} 
                                    onClick={() => handleOpenForm(key)} 
                                    className="flex flex-col items-center justify-center gap-4 p-6 bg-slate-800/50 hover:bg-cyan-500/20 rounded-2xl transition-all border border-slate-700/50 hover:border-cyan-500/50 group"
                                >
                                    <div className="text-4xl text-slate-400 group-hover:text-cyan-400 transform group-hover:scale-110 transition-all">
                                        {PASSWORD_CATEGORIES[key].icon}
                                    </div>
                                    <span className="font-bold text-slate-300 group-hover:text-white text-sm">{PASSWORD_CATEGORIES[key].name}</span>
                                </button>
                            ))}
                        </div>
                        <button onClick={() => setCategorySelectionMode(false)} className="mt-10 w-full py-4 text-slate-400 hover:text-white font-bold bg-slate-800 hover:bg-slate-700 rounded-xl transition-all border border-slate-700 uppercase tracking-widest text-xs">Cancelar</button>
                    </div>
                 </div>
            )}

            {formMode && permissionLevel === 'edit' && (
                <PasswordFormModal
                    mode={formMode.mode}
                    category={formMode.category}
                    item={currentItem}
                    onClose={handleCloseForm}
                    collectionPath={passwordsCollectionPath}
                />
            )}

            {viewItem && (
                <CredentialDetailModal 
                    item={viewItem} 
                    onClose={() => setViewItem(null)}
                    onEdit={() => handleOpenForm(viewItem.category, 'edit', viewItem)}
                    onDelete={() => handleDeletePassword(viewItem.id, getPasswordDisplayName(viewItem))}
                    permissionLevel={permissionLevel}
                />
            )}

            <header className="mb-12 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                <div>
                    <h1 className="text-5xl font-black text-white tracking-tight">Cofre de<span className="text-cyan-400 ml-3">Senhas</span></h1>
                    <p className="text-slate-500 mt-2 font-medium">As suas senhas pessoais, seguras e acessíveis apenas por si.</p>
                </div>
                 {permissionLevel === 'edit' && (
                    <button onClick={() => setCategorySelectionMode(true)} className="bg-cyan-600 hover:bg-cyan-500 text-white font-black py-4 px-8 rounded-2xl transition-all shadow-xl shadow-cyan-900/30 active:scale-95 flex items-center gap-3">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4" /></svg>
                        NOVA SENHA
                    </button>
                 )}
            </header>
            
            <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-8 text-white">
                {loading ? (
                    <div className="col-span-full flex justify-center py-20">
                        <div className="animate-spin rounded-full h-14 w-14 border-b-2 border-cyan-500 shadow-xl shadow-cyan-500/20"></div>
                    </div>
                ) : Object.keys(groupedPasswords).length === 0 ? (
                    <div className="col-span-full flex flex-col items-center justify-center py-20 opacity-30">
                        <div className="text-6xl mb-4">🔐</div>
                        <p className="font-bold tracking-widest uppercase text-slate-500">Nenhuma senha cadastrada ainda.</p>
                    </div>
                ) : Object.keys(groupedPasswords).map(key => {
                    const group = groupedPasswords[key];
                    return (
                        <div key={key} className="bg-slate-800/40 rounded-[2rem] border border-slate-700/50 flex flex-col h-[500px] overflow-hidden transition-all hover:border-cyan-500/30 hover:shadow-2xl hover:shadow-cyan-500/5 group/card backdrop-blur-sm">
                            <div className="p-6 border-b border-slate-700/50 bg-slate-800/80 flex items-center justify-between">
                                <h2 className="text-xl font-extrabold text-cyan-300 truncate flex items-center gap-3">
                                    <div className="text-cyan-400 opacity-60 group-hover/card:opacity-100 transition-opacity">
                                        {group.icon}
                                    </div>
                                    {group.name}
                                </h2>
                                <div className="flex items-center gap-3">
                                    <input 
                                        type="text" 
                                        placeholder="Pesquisar..." 
                                        value={categorySearchText[key] || ''}
                                        onChange={(e) => setCategorySearchText({...categorySearchText, [key]: e.target.value})}
                                        className="bg-slate-900 border border-slate-700 rounded-lg py-1.5 px-3 text-white text-xs w-32 focus:w-48 transition-all outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 placeholder-slate-500 font-sans"
                                    />
                                    <span className="bg-slate-950 text-cyan-400 text-[10px] font-black px-3 py-1.5 rounded-lg border border-cyan-500/20 shadow-inner">{group.passwords.length}</span>
                                </div>
                            </div>
                            
                            <div className="flex-grow overflow-y-auto p-5 custom-scrollbar bg-slate-900/10 space-y-3">
                                {group.passwords.filter(pw => {
                                    const search = (categorySearchText[key] || '').toLowerCase();
                                    if (!search) return true;
                                    const name = getPasswordDisplayName(pw).toLowerCase();
                                    const sub = getPasswordDisplaySubtext(pw).toLowerCase();
                                    return name.includes(search) || sub.includes(search);
                                }).map(pw => (
                                    <div 
                                        key={pw.id}
                                        onClick={() => setViewItem(pw)}
                                        className="group/item relative bg-slate-800/80 border border-slate-700/60 p-5 rounded-2xl cursor-pointer transition-all hover:bg-slate-700 hover:border-cyan-500/50 hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-950/40 active:scale-[0.98]"
                                    >
                                        <div className="flex items-center justify-between gap-4">
                                            <div className="flex flex-col gap-1 min-w-0">
                                                <span className="text-slate-100 font-extrabold truncate text-base leading-tight group-hover/item:text-cyan-100">{getPasswordDisplayName(pw)}</span>
                                                <span className="text-[10px] text-slate-500 font-bold tracking-wider uppercase group-hover/item:text-cyan-400/70 transition-colors truncate">
                                                    {getPasswordDisplaySubtext(pw) || 'Sem subtexto'}
                                                </span>
                                            </div>
                                            <div className="bg-slate-700/40 p-2 rounded-xl text-slate-500 group-hover/item:text-cyan-400 group-hover/item:bg-cyan-400/10 transition-all border border-transparent group-hover/item:border-cyan-500/20">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" /></svg>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// --- Componente de Visualização de Detalhes ---
function CredentialDetailModal({ item, onClose, onEdit, onDelete, permissionLevel }) {
    if (!item) return null;
    const categoryConfig = PASSWORD_CATEGORIES[item.category];

    const copyToClipboard = (text) => {
        if (!text) return;
        navigator.clipboard.writeText(String(text));
    };

    return (
        <div className="fixed inset-0 bg-slate-950/90 backdrop-blur-md flex justify-center items-center z-50 p-4 animate-in fade-in duration-300">
            <div className="bg-slate-900 border border-slate-700/50 rounded-[2.5rem] shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-300">
                <header className="p-8 border-b border-slate-800 bg-slate-800/20 flex justify-between items-center">
                    <div className="flex items-center gap-5">
                        <div className="w-16 h-16 bg-cyan-500/10 rounded-2xl flex items-center justify-center text-3xl text-cyan-400 border border-cyan-500/20">
                            {categoryConfig?.icon}
                        </div>
                        <div>
                            <h3 className="text-2xl font-black text-white tracking-tight">{item.data[categoryConfig.fields[0].name] || 'Credencial'}</h3>
                            <div className="flex items-center gap-2 mt-1">
                                <span className="text-[10px] font-black text-cyan-400 uppercase tracking-[0.2em]">{categoryConfig?.name}</span>
                            </div>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-slate-500 hover:text-white p-3 rounded-2xl hover:bg-slate-800 transition-all border border-transparent hover:border-slate-700">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </header>

                <div className="flex-grow overflow-y-auto p-8 custom-scrollbar">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-8">
                        {categoryConfig.fields.map((field) => {
                            const value = item.data[field.name];
                            const isFullWidth = field.type === 'textarea' || field.name === 'totpSecret';
                            
                            return (
                                <div key={field.name} className={`${isFullWidth ? 'md:col-span-2' : ''} bg-slate-800/30 p-4 rounded-2xl border border-slate-700/30 group/field transition-all hover:border-cyan-500/20`}>
                                    <div className="flex justify-between items-center mb-1">
                                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest leading-none">{field.label}</label>
                                        {(field.type !== 'totp' && value) && (
                                            <button 
                                                onClick={() => copyToClipboard(value)}
                                                className="text-[10px] font-bold text-cyan-400/0 group-hover/field:text-cyan-400 transition-all hover:scale-110 flex items-center gap-1 uppercase"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" /></svg>
                                                Copiar
                                            </button>
                                        )}
                                    </div>
                                    {field.name === 'totpSecret' ? (
                                        <div className="space-y-4">
                                            <QrCodeInput 
                                                value={value} 
                                                onChange={() => {}} 
                                                placeholder={field.label}
                                                readOnly={true}
                                            />
                                            <div className="bg-cyan-500/5 p-4 rounded-xl border border-cyan-500/10">
                                                <TOTPDisplay secret={value} />
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-white font-bold break-all text-sm">
                                            {field.type === 'password' ? (
                                                <PasswordDisplay value={value} />
                                            ) : (
                                                <LinkifyText text={String(value || '')} />
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        {item.data.customFields && item.data.customFields.map((field, idx) => (
                            <div key={`custom-${idx}`} className="md:col-span-2 bg-slate-800/30 p-4 rounded-2xl border border-slate-700/30 group/field transition-all hover:border-cyan-500/20">
                                <div className="flex justify-between items-center mb-1">
                                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest leading-none">{field.name}</label>
                                    <button 
                                        onClick={() => copyToClipboard(field.value)}
                                        className="text-[10px] font-bold text-cyan-400/0 group-hover/field:text-cyan-400 transition-all hover:scale-110 flex items-center gap-1 uppercase"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" /></svg>
                                        Copiar
                                    </button>
                                </div>
                                <div className="text-white font-bold break-all text-sm">
                                    <LinkifyText text={String(field.value || '')} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <footer className="p-8 border-t border-slate-800 bg-slate-800/20 flex flex-col sm:flex-row gap-4">
                    {permissionLevel === 'edit' && (
                        <div className="flex flex-grow gap-3">
                            <button 
                                onClick={onEdit} 
                                className="flex-grow bg-cyan-600 hover:bg-cyan-500 text-white font-bold py-3 px-6 rounded-2xl transition-all shadow-xl shadow-cyan-900/40 flex items-center justify-center gap-2 group/edit"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 transition-transform group-hover:scale-110" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.5L16.732 3.732z" /></svg>
                                <span className="tracking-tight">Editar Registro</span>
                            </button>
                            <button 
                                onClick={onDelete} 
                                className="bg-red-500/10 hover:bg-red-600 text-red-500 hover:text-white border border-red-500/30 font-bold py-3 px-5 rounded-2xl transition-all flex items-center justify-center min-w-[60px] group/delete"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 transition-transform group-hover/delete:scale-110" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                            </button>
                        </div>
                    )}
                    <button 
                        onClick={onClose} 
                        className="bg-slate-700 hover:bg-slate-600 text-white font-bold py-4 px-8 rounded-2xl transition-all sm:w-auto"
                    >
                        Fechar
                    </button>
                </footer>
            </div>
        </div>
    );
}

// --- Modal de Formulário de Senhas ---
function PasswordFormModal({ mode, category, item, onClose, collectionPath }) {
    const isEdit = mode === 'edit';
    const [formData, setFormData] = useState({});
    const [customFields, setCustomFields] = useState([]);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        const initialData = {};
        PASSWORD_CATEGORIES[category].fields.forEach(field => {
            initialData[field.name] = isEdit && item.category === category ? (item.data[field.name] || '') : '';
        });
        setFormData(initialData);
        setCustomFields(isEdit && item.data.customFields ? [...item.data.customFields] : []);
    }, [category, item, isEdit]);

    const handleFormChange = (e) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleAddCustomField = () => {
        setCustomFields([...customFields, { name: '', value: '' }]);
    };

    const handleCustomFieldChange = (index, field, value) => {
        const updated = [...customFields];
        updated[index][field] = value;
        setCustomFields(updated);
    };

    const handleRemoveCustomField = (index) => {
        setCustomFields(customFields.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        
        if (isSaving) return;
        setIsSaving(true);

        try {
            const dataToSave = {
                category,
                data: {
                    ...formData,
                    customFields: customFields.filter(f => f.name.trim() !== '')
                },
                updatedAt: new Date(),
            };

            const normalizedPath = collectionPath.startsWith('/') ? collectionPath.substring(1) : collectionPath;

            if (isEdit) {
                await updateDoc(doc(db, normalizedPath, item.id), dataToSave);
            } else {
                dataToSave.createdAt = new Date();
                await addDoc(collection(db, normalizedPath), dataToSave);
            }
            onClose();
        } catch (error) {
            console.error("Erro ao salvar senha:", error);
            alert(`Erro ao salvar: ${error.message}`);
        } finally {
            setIsSaving(false);
        }
    };

    const renderField = (field) => {
        const id = `${field.name}-${isEdit ? `edit-${item.id}` : 'add'}`;
        if (field.type === 'password' && field.name !== 'totpSecret') {
             return (
                <div key={field.name}>
                    <label htmlFor={id} className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2 ml-1">{field.label}</label>
                    <PasswordGeneratorInput 
                        id={id} 
                        name={field.name} 
                        value={formData[field.name] || ''} 
                        onChange={handleFormChange} 
                        placeholder={field.placeholder}
                    />
                </div>
            );
        }
        if (field.type === 'password' && field.name === 'totpSecret') {
            return (
                <div key={field.name} className="space-y-2 md:col-span-2">
                    <label htmlFor={id} className="block text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">{field.label}</label>
                    <QrCodeInput
                        id={id}
                        name={field.name}
                        value={formData[field.name] || ''}
                        onChange={handleFormChange}
                        placeholder={field.placeholder}
                    />
                </div>
            );
        }
        const Component = field.type === 'textarea' ? 'textarea' : 'input';
        return (
            <div key={field.name}>
                <label htmlFor={id} className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2 ml-1">{field.label}</label>
                <Component 
                    id={id} 
                    name={field.name} 
                    type={field.type} 
                    rows={field.type === 'textarea' ? 3 : null} 
                    value={formData[field.name] || ''} 
                    onChange={handleFormChange} 
                    placeholder={field.placeholder} 
                    className="w-full bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-white focus:ring-2 focus:ring-cyan-500/50 focus:outline-none transition-all placeholder:text-slate-600 shadow-inner"
                />
            </div>
        );
    };

    return (
        <div className="fixed inset-0 bg-slate-950/90 backdrop-blur-md flex justify-center items-center z-50 p-4 animate-in fade-in duration-300">
            <div className="bg-slate-900 border border-slate-700/50 rounded-3xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-300">
                <header className="p-7 border-b border-slate-800 bg-slate-800/20 flex justify-between items-center">
                    <div>
                        <h3 className="text-2xl font-black text-white uppercase tracking-tight">{isEdit ? `Editar Credencial` : `Nova Credencial`}</h3>
                        <div className="flex items-center gap-2 mt-1">
                            <span className="text-[10px] font-black text-cyan-400 uppercase tracking-widest leading-none">{PASSWORD_CATEGORIES[category].name}</span>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-slate-500 hover:text-white p-2 rounded-xl hover:bg-slate-800 transition-all border border-transparent hover:border-slate-700">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </header>

                <form onSubmit={handleSubmit} className="p-8 overflow-y-auto flex-grow custom-scrollbar space-y-8">
                    <div className="space-y-6">
                        {PASSWORD_CATEGORIES[category].fields.map(renderField)}

                        {/* Seção de Campos Customizados */}
                        <div className="pt-6 border-t border-slate-800 space-y-6">
                            <div className="flex justify-between items-center">
                                <h4 className="text-xs font-black text-slate-600 uppercase tracking-widest flex items-center gap-2">
                                    <span className="w-8 h-px bg-slate-800"></span>
                                    Campos Adicionais
                                </h4>
                                <button type="button" onClick={handleAddCustomField} className="text-[10px] font-black bg-slate-800 hover:bg-slate-700 text-cyan-400 px-3 py-1.5 rounded-lg border border-slate-700 transition-all uppercase tracking-wider shadow-lg active:scale-95">
                                    + Adicionar campo
                                </button>
                            </div>
                            
                            <div className="space-y-4">
                                {customFields.map((field, index) => (
                                    <div key={index} className="flex gap-3 group animate-in slide-in-from-left-4 duration-200">
                                        <input 
                                            placeholder="Título"
                                            value={field.name}
                                            onChange={(e) => handleCustomFieldChange(index, 'name', e.target.value)}
                                            className="flex-1 bg-slate-800/30 border border-slate-700/50 rounded-xl px-4 py-3 text-sm text-white focus:ring-2 focus:ring-cyan-500/30 focus:outline-none transition-all"
                                        />
                                        <input 
                                            placeholder="Conteúdo"
                                            value={field.value}
                                            onChange={(e) => handleCustomFieldChange(index, 'value', e.target.value)}
                                            className="flex-[1.5] bg-slate-800/30 border border-slate-700/50 rounded-xl px-4 py-3 text-sm text-white focus:ring-2 focus:ring-cyan-500/30 focus:outline-none transition-all"
                                        />
                                        <button type="button" onClick={() => handleRemoveCustomField(index)} className="p-3 text-slate-600 hover:text-red-400 transition-colors active:scale-90">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </form>

                <footer className="p-8 border-t border-slate-800 bg-slate-800/20 flex justify-end gap-4">
                    <button type="button" onClick={onClose} disabled={isSaving} className="px-8 py-4 bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold rounded-2xl transition-all border border-slate-700 active:scale-95">
                        Cancelar
                    </button>
                    <button 
                        type="submit" 
                        onClick={handleSubmit} 
                        disabled={isSaving}
                        className="px-10 py-4 bg-cyan-600 hover:bg-cyan-500 text-white font-black rounded-2xl transition-all shadow-xl shadow-cyan-900/40 disabled:opacity-50 flex items-center gap-3 active:scale-95"
                    >
                        {isSaving ? (
                            <>
                                <div className="animate-spin h-4 w-4 border-2 border-white/30 border-t-white rounded-full"></div>
                                SALVANDO...
                            </>
                        ) : (
                            <>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor font-black font-black"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                                SALVAR
                            </>
                        )}
                    </button>
                </footer>
            </div>
        </div>
    );
}
